import React, { useState, useEffect, useRef, useMemo } from "react";
import { ApiService } from "../../services/api";
import { Search, User, Mail, Building, X, ChevronDown, Check } from "lucide-react";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: {
    id: string;
    name: string;
    color?: string;
  };
  media?: {
    id: string;
    url: string;
  };
}

interface UserSelectorProps {
  value?: string | null;
  onChange: (userId: string, user?: User) => void;
  placeholder?: string;
  excludeIds?: string[];
  className?: string;
  disabled?: boolean;
  required?: boolean;
  allowClear?: boolean;
  autoOpen?: boolean;
  inline?: boolean;
  hideSelected?: boolean;
  departmentId?: string | null;
}

export default function UserSelector({
  value = null,
  onChange,
  placeholder = "Kullanıcı Seçin",
  excludeIds = [],
  className = "",
  disabled = false,
  required = false,
  allowClear = true,
  autoOpen = false,
  inline = false,
  hideSelected = false,
  departmentId = null,
}: UserSelectorProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [searchTerm, setSearchTerm] = useState("");
  const [cachedUser, setCachedUser] = useState<User | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value) {
      setCachedUser(null);
    }
  }, [value]);

  useEffect(() => {
    const searchUsers = async () => {
      try {
        setLoading(true);
        const response = await ApiService.getUsers(1, 50, searchTerm.trim() || undefined, departmentId || undefined);
        const users = Array.isArray(response) ? response : response?.results || response?.data || [];
        setUsers(users || []);
      } catch (error) {
        console.error("Kullanıcılar yüklenirken hata:", error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, departmentId]);

  const selectedUser = users.find((u) => u.id === value) || cachedUser;

  const filteredUsers = useMemo(() => {
    if (hideSelected) {
      return users.filter((user) => !excludeIds.includes(user.id));
    }
    return users;
  }, [users, excludeIds, hideSelected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (userId: string) => {
    const selectedUser = users.find((u) => u.id === userId);
    if (selectedUser) {
      setCachedUser(selectedUser);
    }
    onChange(userId, selectedUser);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    if (allowClear && !required) {
      setCachedUser(null);
      onChange("", undefined);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  if (inline) {
    return (
      <div className={`${className}`} ref={dropdownRef}>
        <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Kullanıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                <span className="text-xs mt-1 block">Yükleniyor...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">{searchTerm ? "Sonuç Bulunamadı" : "Kullanıcı Bulunamadı"}</div>
            ) : (
              <>
                {filteredUsers.map((user) => {
                  const isExcluded = excludeIds.includes(user.id);
                  const isCurrentValue = value === user.id;

                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelect(user.id)}
                      className={`
                        w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3
                        ${isCurrentValue
                          ? "bg-blue-50 text-blue-600"
                          : isExcluded && !hideSelected
                            ? "bg-green-50 text-green-600 border-l-2 border-green-400"
                            : "text-gray-900"
                        }
                      `}>
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {user.media?.url ? (
                          <img src={user.media.url} alt={`${user.firstName} ${user.lastName}`} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-medium text-xs">
                            {user.firstName?.charAt(0)}
                            {user.lastName?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">
                          {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "İsimsiz Kullanıcı"}
                        </div>
                        <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                        {user.department && (
                          <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {user.department.name}
                          </div>
                        )}
                      </div>
                      {(isCurrentValue || (isExcluded && !hideSelected)) && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-4 py-2 text-left border rounded-lg focus:ring-2 focus:ring-blue-500 
          transition-colors duration-200 flex items-center justify-between
          ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200" : "bg-white hover:border-gray-400 border-gray-300"}
          ${isOpen ? "ring-2 ring-blue-500 border-blue-500" : ""}
        `}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedUser ? (
            <>
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                {selectedUser.media?.url ? (
                  <img
                    src={selectedUser.media.url}
                    alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white font-medium text-[10px]">
                    {selectedUser.firstName?.charAt(0)}
                    {selectedUser.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <span className="truncate text-gray-900 text-sm">
                  {`${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() || "İsimsiz Kullanıcı"}
                </span>
                <div className="text-xs text-gray-500 truncate">{selectedUser.email}</div>
              </div>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {selectedUser && allowClear && !required && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }
              }}>
              <X className="w-3 h-3 text-gray-400" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Kullanıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-44">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                <span className="text-xs mt-1 block">Yükleniyor...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">{searchTerm ? "Sonuç Bulunamadı" : "Kullanıcı Bulunamadı"}</div>
            ) : (
              <>
                {filteredUsers.map((user) => {
                  const isExcluded = excludeIds.includes(user.id);
                  const isCurrentValue = value === user.id;

                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelect(user.id)}
                      className={`
                        w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3
                        ${isCurrentValue
                          ? "bg-blue-50 text-blue-600"
                          : isExcluded && !hideSelected
                            ? "bg-green-50 text-green-600 border-l-2 border-green-400"
                            : "text-gray-900"
                        }
                      `}>
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {user.media?.url ? (
                          <img src={user.media.url} alt={`${user.firstName} ${user.lastName}`} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-medium text-xs">
                            {user.firstName?.charAt(0)}
                            {user.lastName?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">
                          {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "İsimsiz Kullanıcı"}
                        </div>
                        <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                        {user.department && (
                          <div className="text-xs text-gray-400 truncate flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {user.department.name}
                          </div>
                        )}
                      </div>
                      {(isCurrentValue || (isExcluded && !hideSelected)) && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
